import {
  IconExternalLink,
  IconAlertCircle,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
} from "@tanstack/react-router";
import { FaGithub } from "react-icons/fa";

import { agentTypeConfigs } from "@/agents/constants/agent-type-configs";
import { cn } from "@/infrastructure/lib/utils";
import { useTRPC } from "@/infrastructure/trpc/react";
import { AppLayout } from "@/shared/layout";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
  EmptyMedia,
} from "@/shared/ui/empty";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import {
  AgentTypes,
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  getValidPageSize,
  type PageSize,
} from "@vivus/common";

type IssuesSearch = {
  status: "open" | "closed" | "all";
  agentType: string | undefined;
  page: number;
  limit?: PageSize;
};

export const Route = createFileRoute("/app/issues")({
  validateSearch: (
    search: Record<string, unknown>
  ): IssuesSearch => ({
    status:
      (search.status as "open" | "closed" | "all") ?? "all",
    agentType: search.agentType as string | undefined,
    page: Number(search.page) || 1,
    limit: getValidPageSize(search.limit),
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => {
    const limit = deps.limit ?? DEFAULT_PAGE_SIZE;
    const offset = (deps.page - 1) * limit;

    await context.queryClient
      .query(
        context.trpc.agents.getAllIssues.queryOptions({
          status: deps.status,
          agentType: deps.agentType as
            | typeof AgentTypes.AzureDevOpsPipeline
            | typeof AgentTypes.K8sIssues
            | typeof AgentTypes.K8sOptimization
            | typeof AgentTypes.Sentry
            | undefined,
          limit,
          offset,
        })
      )
      .catch(() => undefined);
  },
  component: IssuesPage,
});

function formatDate(date: Date | null): string {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function IssuesPage() {
  const trpc = useTRPC();
  const {
    status,
    agentType,
    page,
    limit: rawLimit,
  } = Route.useSearch();
  const navigate = Route.useNavigate();

  const limit = rawLimit ?? DEFAULT_PAGE_SIZE;
  const offset = (page - 1) * limit;

  const { data } = useSuspenseQuery(
    trpc.agents.getAllIssues.queryOptions({
      status,
      agentType: agentType as
        | typeof AgentTypes.AzureDevOpsPipeline
        | typeof AgentTypes.K8sIssues
        | typeof AgentTypes.K8sOptimization
        | typeof AgentTypes.Sentry
        | undefined,
      limit,
      offset,
    })
  );

  const totalPages = Math.ceil(data.total / limit);

  const updateFilters = (updates: {
    status?: "open" | "closed" | "all";
    agentType?: string | undefined;
    page?: number;
    limit?: PageSize;
  }) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        ...updates,
        // Reset to page 1 when filters or limit change
        page:
          updates.page ??
          (updates.status ||
          updates.agentType !== undefined ||
          updates.limit
            ? 1
            : prev.page),
      }),
    });
  };

  return (
    <AppLayout
      title="GitHub Issues"
      description="All issues created by vivus agents"
    >
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Status:
            </span>
            <Select
              value={status}
              onValueChange={(value) =>
                updateFilters({
                  status: value as
                    | "open"
                    | "closed"
                    | "all",
                })
              }
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="closed">
                  Closed
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Source:
            </span>
            <Select
              value={agentType ?? "all"}
              onValueChange={(value) => {
                const newAgentType =
                  value === "all" || value === null
                    ? undefined
                    : value;
                updateFilters({ agentType: newAgentType });
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  All sources
                </SelectItem>
                <SelectItem
                  value={AgentTypes.AzureDevOpsPipeline}
                >
                  Azure DevOps
                </SelectItem>
                <SelectItem value={AgentTypes.K8sIssues}>
                  Kubernetes
                </SelectItem>
                <SelectItem
                  value={AgentTypes.K8sOptimization}
                >
                  K8s Optimization
                </SelectItem>
                <SelectItem value={AgentTypes.Sentry}>
                  Sentry
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Per page:
            </span>
            <Select
              value={String(limit)}
              onValueChange={(value) =>
                updateFilters({
                  limit: Number(value) as PageSize,
                })
              }
            >
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem
                    key={size}
                    value={String(size)}
                  >
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="ml-auto text-sm text-muted-foreground">
            {data.total} issue{data.total !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Issues List */}
        {data.issues.length > 0 ? (
          <Card
            variant="compact"
            className="overflow-hidden"
          >
            <CardContent variant="divideP0">
              {data.issues.map((issue) => {
                const config = issue.agentType
                  ? agentTypeConfigs[issue.agentType]
                  : undefined;

                return (
                  <a
                    key={issue.id}
                    href={issue.issueUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "group flex items-center gap-4 px-4 py-3 transition-colors",
                      "hover:bg-muted/50",
                      "border-l-2",
                      issue.status === "open"
                        ? "border-l-warning-border"
                        : "border-l-success-border"
                    )}
                  >
                    {/* Agent type icon */}
                    <div
                      className={cn(
                        "shrink-0 rounded p-2",
                        config?.bgColor ?? "bg-muted"
                      )}
                    >
                      {config ? (
                        <config.icon className="size-4" />
                      ) : (
                        <IconAlertCircle className="size-4" />
                      )}
                    </div>

                    {/* Issue info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium">
                          {issue.issueTitle}
                        </span>
                        <IconExternalLink className="size-3 shrink-0 text-muted-foreground/50 opacity-0 transition-opacity group-hover:opacity-100" />
                      </div>
                      <div className="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <FaGithub className="size-3" />
                          {issue.repositoryOwner}/
                          {issue.repositoryName}
                          <span className="text-muted-foreground/50">
                            #{issue.issueNumber}
                          </span>
                        </span>
                        {issue.projectId &&
                          issue.projectName && (
                            <>
                              <span className="text-muted-foreground/50">
                                •
                              </span>
                              <Link
                                to="/app/project/$id"
                                params={{
                                  id: issue.projectId,
                                }}
                                className="hover:underline"
                                onClick={(e) =>
                                  e.stopPropagation()
                                }
                              >
                                {issue.projectName}
                              </Link>
                            </>
                          )}
                      </div>
                    </div>

                    {/* Status & source */}
                    <div className="flex shrink-0 items-center gap-3">
                      <Badge
                        size="compact"
                        variant={
                          issue.status === "open"
                            ? "warning"
                            : "secondary"
                        }
                      >
                        {issue.status}
                      </Badge>
                      <Badge
                        size="compact"
                        variant="outline"
                      >
                        {config?.icon && <config.icon />}
                        {config?.label}
                      </Badge>
                      <span className="w-32 text-right text-xs text-muted-foreground tabular-nums">
                        {formatDate(issue.createdAt)}
                      </span>
                    </div>
                  </a>
                );
              })}
            </CardContent>
          </Card>
        ) : (
          <Empty variant="taller">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FaGithub className="size-5" />
              </EmptyMedia>
              <EmptyTitle>No issues found</EmptyTitle>
              <EmptyDescription>
                {status !== "all" || agentType
                  ? "Try adjusting your filters"
                  : "Issues created by vivus agents will appear here"}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() =>
                  updateFilters({ page: page - 1 })
                }
              >
                <IconChevronLeft className="mr-1 size-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() =>
                  updateFilters({ page: page + 1 })
                }
              >
                Next
                <IconChevronRight className="ml-1 size-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
